// 自動選択 (マジックワンド) の flood fill。
// 参照画像は密な RGBA (文書サイズ or レイヤー矩形)。許容値はチャンネルごとの差の最大値。

import type { Rect } from '@/types/geometry'
import { emptyMask, trimMask, type MaskData } from './mask'

export interface WandOptions {
  /** 0..255 */
  tolerance: number
  contiguous: boolean
  antialias: boolean
}

/**
 * @param rgba 参照画像 (rect の範囲)
 * @param seed 文書座標のクリック位置
 */
export function magicWand(rgba: Uint8ClampedArray, rect: Rect, seed: { x: number; y: number }, opts: WandOptions): MaskData | null {
  const { w, h } = rect
  const sx = Math.floor(seed.x) - rect.x
  const sy = Math.floor(seed.y) - rect.y
  if (sx < 0 || sy < 0 || sx >= w || sy >= h) return null
  const si = (sy * w + sx) * 4
  const r0 = rgba[si]!, g0 = rgba[si + 1]!, b0 = rgba[si + 2]!, a0 = rgba[si + 3]!
  const tol = opts.tolerance
  const match = (i: number) => {
    const o = i * 4
    const a = rgba[o + 3]!
    if (a0 === 0) return a === 0 || Math.abs(a - a0) <= tol
    return Math.abs(rgba[o]! - r0) <= tol && Math.abs(rgba[o + 1]! - g0) <= tol && Math.abs(rgba[o + 2]! - b0) <= tol && Math.abs(a - a0) <= tol
  }
  const out = emptyMask(rect)
  if (!opts.contiguous) {
    for (let i = 0; i < w * h; i++) if (match(i)) out.data[i] = 255
  } else {
    // スキャンライン flood
    const visited = new Uint8Array(w * h)
    const stack: number[] = [sy * w + sx]
    while (stack.length) {
      const idx = stack.pop()!
      if (visited[idx] || !match(idx)) continue
      const y = Math.floor(idx / w)
      let x0 = idx % w
      let x1 = x0
      while (x0 > 0 && !visited[idx - (idx % w) + x0 - 1] && match(y * w + x0 - 1)) x0--
      while (x1 < w - 1 && !visited[y * w + x1 + 1] && match(y * w + x1 + 1)) x1++
      for (let x = x0; x <= x1; x++) {
        const i = y * w + x
        visited[i] = 1
        out.data[i] = 255
        if (y > 0 && !visited[i - w] && match(i - w)) stack.push(i - w)
        if (y < h - 1 && !visited[i + w] && match(i + w)) stack.push(i + w)
      }
    }
  }
  if (opts.antialias) softenEdges(out)
  return trimMask(out)
}

/** 2 値マスクの内側の縁を 3x3 平均で滑らかにする (外側には広げない) */
function softenEdges(m: MaskData): void {
  const { w, h } = m.rect
  const src = new Uint8ClampedArray(m.data)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x
      const v = src[i]!
      if (v === 0) continue
      // 内側の境界ピクセルだけ処理 (4 近傍のいずれかが外側)
      const l = x > 0 ? src[i - 1]! : v, r = x < w - 1 ? src[i + 1]! : v
      const u = y > 0 ? src[i - w]! : v, d = y < h - 1 ? src[i + w]! : v
      if (l === v && r === v && u === v && d === v) continue
      let sum = 0, n = 0
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx, yy = y + dy
          if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue
          sum += src[yy * w + xx]!
          n++
        }
      }
      m.data[i] = Math.round(sum / n)
    }
  }
}
